import React from "react";
import { Box, Button, Divider, Popover, Typography } from "@mui/material";
import { ContentCopy as CopyIcon } from "@mui/icons-material";
import { STRONG_DROPDOWN_PAPER_SX } from "../../common/dropdownPaper";

/**
 * The full text of one cell. The grid keeps every value on a single line so the
 * row height stays fixed, which means a long note or a value with line breaks
 * in it is only readable here - the native title tooltip drops the breaks and
 * truncates on its own terms.
 */

export interface CellDetail {
  anchor: HTMLElement;
  columnLabel: string;
  rowNumber: number;
  value: string;
}

interface CellDetailPopoverProps {
  detail: CellDetail | null;
  container?: HTMLElement | null;
  zIndex?: number;
  onClose: () => void;
  onCopy: (value: string) => void;
}

const CellDetailPopover: React.FC<CellDetailPopoverProps> = ({
  detail,
  container,
  zIndex,
  onClose,
  onCopy,
}) => (
  <Popover
    open={!!detail}
    anchorEl={detail ? detail.anchor : null}
    onClose={onClose}
    container={container ?? undefined}
    anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
    slotProps={{
      paper: {
        elevation: 0,
        sx: [
          STRONG_DROPDOWN_PAPER_SX,
          { width: { xs: "calc(100vw - 32px)", sm: 420 }, maxWidth: "100%" },
        ],
      },
    }}
    sx={zIndex != null ? { zIndex } : undefined}
  >
    {detail && (
      <Box>
        <Typography
          variant="subtitle2"
          sx={{ px: 1.5, pt: 1.5, pb: 1, wordBreak: "break-word" }}
        >
          {detail.columnLabel} - row {detail.rowNumber.toLocaleString()}
        </Typography>
        <Divider />
        <Box
          component="pre"
          sx={{
            m: 0,
            px: 1.5,
            py: 1.5,
            maxHeight: 320,
            overflow: "auto",
            fontFamily:
              'ui-monospace, SFMono-Regular, Menlo, Monaco, "Cascadia Mono", "Liberation Mono", monospace',
            fontSize: 13,
            lineHeight: 1.5,
            whiteSpace: "pre-wrap",
            wordBreak: "break-word",
          }}
        >
          {detail.value === "" ? "(empty)" : detail.value}
        </Box>
        <Divider />
        <Box sx={{ display: "flex", justifyContent: "flex-end", p: 1 }}>
          <Button
            size="small"
            startIcon={<CopyIcon fontSize="small" />}
            onClick={() => onCopy(detail.value)}
            sx={{ textTransform: "none" }}
          >
            Copy value
          </Button>
        </Box>
      </Box>
    )}
  </Popover>
);

export default CellDetailPopover;
